import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API_BASE, formatApiError, useAuth } from "../components/AuthContext";
import { toast } from "sonner";
import { Building2, Image as ImageIcon, Palette, Loader2, Upload, Trash2, Check } from "lucide-react";
import { useDocusealTemplates } from "../hooks/useDocusealTemplates";

const FY_MONTHS = [
  { v: 1, label: "January" }, { v: 2, label: "February" }, { v: 3, label: "March" },
  { v: 4, label: "April" }, { v: 5, label: "May" }, { v: 6, label: "June" },
  { v: 7, label: "July" }, { v: 8, label: "August" }, { v: 9, label: "September" },
  { v: 10, label: "October" }, { v: 11, label: "November" }, { v: 12, label: "December" },
];

const TZ_PRESETS = [
  "Australia/Sydney", "Australia/Melbourne", "Australia/Brisbane",
  "Australia/Perth", "Australia/Adelaide",
  "America/New_York", "America/Chicago", "America/Los_Angeles",
  "Europe/London", "Europe/Berlin", "Asia/Singapore", "Asia/Dubai",
];

export default function OrganizationSettings() {
  const { state, refresh } = useAuth();
  const [org, setOrg] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    let cancel = false;
    axios.get(`${API_BASE}/orgs/me`)
      .then((r) => { if (!cancel) { setOrg(r.data); setDraft({
        name: r.data.name || "",
        brand_color: r.data.brand_color || "#c0492a",
        address: r.data.address || "",
        phone: r.data.phone || "",
        tax_id: r.data.tax_id || "",
        time_zone: r.data.time_zone || "Australia/Sydney",
        fiscal_year_start_month: r.data.fiscal_year_start_month || 7,
        default_currency: r.data.default_currency || "AUD",
        website_url: r.data.website_url || "",
        social_facebook: r.data.social_facebook || "",
        social_instagram: r.data.social_instagram || "",
        social_tiktok: r.data.social_tiktok || "",
        social_linkedin: r.data.social_linkedin || "",
        social_google: r.data.social_google || "",
        slack_webhook_url: r.data.slack_webhook_url || "",
        default_esign_provider: r.data.default_esign_provider || "builtin",
        default_docuseal_template_id: r.data.default_docuseal_template_id || "",
      }); } })
      .catch((e) => toast.error(formatApiError(e)));
    return () => { cancel = true; };
  }, []);

  const isAdmin = ["owner", "manager"].includes(state?.user?.role);

  const save = async () => {
    if (!isAdmin) return;
    setSaving(true);
    try {
      // Split: eSign defaults live behind a dedicated endpoint (with its own
      // Pydantic validator + role check).
      const { default_esign_provider, default_docuseal_template_id, ...rest } = draft;
      const r = await axios.patch(`${API_BASE}/orgs/me`, rest);
      setOrg(r.data);
      if (default_esign_provider) {
        await axios.patch(`${API_BASE}/orgs/me/esign-config`, {
          default_esign_provider,
          default_docuseal_template_id: default_docuseal_template_id
            ? Number(default_docuseal_template_id) : undefined,
          clear_template: !default_docuseal_template_id,
        });
      }
      toast.success("Organization settings saved");
      refresh && refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const onPickFile = () => fileRef.current?.click();

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo must be under 2 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API_BASE}/orgs/me/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setOrg(r.data);
      toast.success("Logo uploaded");
      refresh && refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    if (!window.confirm("Remove the org logo?")) return;
    try {
      const r = await axios.delete(`${API_BASE}/orgs/me/logo`);
      setOrg(r.data);
      toast.success("Logo removed");
      refresh && refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!org) {
    return <div className="px-10 py-16 text-[var(--ink-3)]"><Loader2 className="animate-spin inline mr-2" size={14} /> Loading…</div>;
  }

  return (
    <div className="px-10 py-16 max-w-3xl" data-testid="org-settings-page">
      <div className="mb-8">
        <div className="overline mb-2 inline-flex items-center gap-1.5">
          <Building2 size={12} /> Organization settings
        </div>
        <h1 className="font-heading text-4xl" style={{ fontWeight: 500 }}>
          Your <em className="italic" style={{ color: "var(--accent)" }}>practice</em>
        </h1>
        <p className="text-[var(--ink-2)] mt-2 max-w-2xl">
          {isAdmin ? "Edit your firm's name, branding, contact details and fiscal year." : "Read-only — only owners and managers can edit these."}
        </p>
      </div>

      {/* Logo card */}
      <div className="card p-6 mb-6" data-testid="org-logo-card">
        <div className="flex items-start gap-6">
          <div className="shrink-0">
            <div
              className="w-24 h-24 rounded-lg border border-[var(--subtle-2)] flex items-center justify-center overflow-hidden bg-[var(--subtle)]"
              data-testid="org-logo-preview"
            >
              {org.logo_url ? (
                <img src={org.logo_url} alt="Org logo" className="max-w-full max-h-full object-contain" />
              ) : (
                <ImageIcon size={32} className="text-[var(--ink-3)]" />
              )}
            </div>
          </div>
          <div className="flex-1">
            <div className="font-medium text-[14px] mb-1">Logo</div>
            <p className="text-[13px] text-[var(--ink-2)] mb-3">
              Shows in the sidebar and on outgoing emails. PNG, JPEG, WebP, AVIF, or SVG. 2 MB max. Recommended 256×256.
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/avif,image/svg+xml"
                onChange={onFileChange}
                className="hidden"
                data-testid="org-logo-file-input"
              />
              <button
                disabled={!isAdmin || uploading}
                onClick={onPickFile}
                className="btn-secondary text-[12px]"
                data-testid="org-logo-upload-btn"
              >
                {uploading ? <><Loader2 className="animate-spin" size={12} /> Uploading…</>
                            : <><Upload size={12} /> {org.logo_url ? "Replace" : "Upload"}</>}
              </button>
              {org.logo_url && isAdmin && (
                <button
                  onClick={removeLogo}
                  className="text-[12px] text-[var(--ink-3)] hover:text-[var(--accent)] inline-flex items-center gap-1"
                  data-testid="org-logo-remove-btn"
                >
                  <Trash2 size={12} /> Remove
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Branding + identity */}
      <div className="card p-6 mb-6 space-y-4" data-testid="org-identity-card">
        <h2 className="text-[14px] font-medium mb-2">Identity</h2>
        <Field
          label="Practice name" testId="org-field-name"
          value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })}
          disabled={!isAdmin}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label="Tax ID (ABN / EIN)" testId="org-field-tax-id"
            value={draft.tax_id} onChange={(v) => setDraft({ ...draft, tax_id: v })}
            disabled={!isAdmin}
          />
          <Field
            label="Phone" testId="org-field-phone"
            value={draft.phone} onChange={(v) => setDraft({ ...draft, phone: v })}
            disabled={!isAdmin}
          />
        </div>
        <Field
          label="Address" testId="org-field-address"
          value={draft.address} onChange={(v) => setDraft({ ...draft, address: v })}
          disabled={!isAdmin} textarea
        />
        <div>
          <label className="block text-[12px] text-[var(--ink-3)] mb-1.5 inline-flex items-center gap-1.5">
            <Palette size={11} /> Brand color
          </label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={draft.brand_color || "#c0492a"}
              onChange={(e) => setDraft({ ...draft, brand_color: e.target.value })}
              disabled={!isAdmin}
              className="w-10 h-10 rounded cursor-pointer"
              data-testid="org-field-color"
            />
            <input
              type="text"
              value={draft.brand_color || ""}
              onChange={(e) => setDraft({ ...draft, brand_color: e.target.value })}
              placeholder="#c0492a"
              disabled={!isAdmin}
              className="input flex-1 max-w-[200px] font-mono"
              data-testid="org-field-color-text"
            />
            <span className="text-[12px] text-[var(--ink-3)]">used on email templates and accents</span>
          </div>
        </div>
      </div>

      {/* Locale */}
      <div className="card p-6 mb-6 space-y-4" data-testid="org-locale-card">
        <h2 className="text-[14px] font-medium mb-2">Locale</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-[12px] text-[var(--ink-3)] mb-1.5">Time zone</label>
            <select
              value={draft.time_zone || "Australia/Sydney"}
              onChange={(e) => setDraft({ ...draft, time_zone: e.target.value })}
              disabled={!isAdmin}
              className="input w-full"
              data-testid="org-field-tz"
            >
              {TZ_PRESETS.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[12px] text-[var(--ink-3)] mb-1.5">Fiscal year starts</label>
            <select
              value={draft.fiscal_year_start_month || 7}
              onChange={(e) => setDraft({ ...draft, fiscal_year_start_month: Number(e.target.value) })}
              disabled={!isAdmin}
              className="input w-full"
              data-testid="org-field-fy"
            >
              {FY_MONTHS.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[12px] text-[var(--ink-3)] mb-1.5">Default currency</label>
            <select
              value={draft.default_currency || "AUD"}
              onChange={(e) => setDraft({ ...draft, default_currency: e.target.value })}
              disabled={!isAdmin}
              className="input w-full"
              data-testid="org-field-currency"
            >
              {["AUD", "USD", "EUR", "GBP", "NZD", "CAD", "SGD", "INR", "JPY"].map((c) =>
                <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="card p-5 space-y-4" data-testid="org-social-links-card">
        <div className="overline text-[10px]">Website &amp; social</div>
        <Field label="Website" value={draft.website_url}
                onChange={(v) => setDraft({ ...draft, website_url: v })}
                testId="org-field-website" disabled={!isAdmin} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Facebook URL" value={draft.social_facebook}
                  onChange={(v) => setDraft({ ...draft, social_facebook: v })}
                  testId="org-field-facebook" disabled={!isAdmin} />
          <Field label="Instagram URL" value={draft.social_instagram}
                  onChange={(v) => setDraft({ ...draft, social_instagram: v })}
                  testId="org-field-instagram" disabled={!isAdmin} />
          <Field label="TikTok URL" value={draft.social_tiktok}
                  onChange={(v) => setDraft({ ...draft, social_tiktok: v })}
                  testId="org-field-tiktok" disabled={!isAdmin} />
          <Field label="LinkedIn URL" value={draft.social_linkedin}
                  onChange={(v) => setDraft({ ...draft, social_linkedin: v })}
                  testId="org-field-linkedin" disabled={!isAdmin} />
          <Field label="Google profile URL" value={draft.social_google}
                  onChange={(v) => setDraft({ ...draft, social_google: v })}
                  testId="org-field-google" disabled={!isAdmin} />
        </div>
      </div>

      <div>
        <h3 className="font-heading text-lg mb-3" style={{ fontWeight: 500 }}>Slack integration</h3>
        <p className="text-[12px] text-[var(--ink-2)] mb-3 max-w-lg">
          Paste an incoming-webhook URL and the weekly overdue digest will cross-post a summary
          to Slack after emails go out.
        </p>
        <Field label="Slack incoming webhook URL"
                placeholder="https://hooks.slack.com/services/T…/B…/…"
                value={draft.slack_webhook_url}
                onChange={(v) => setDraft({ ...draft, slack_webhook_url: v })}
                testId="org-field-slack-webhook" disabled={!isAdmin} />
      </div>

      <EsignDefaultsSection draft={draft} setDraft={setDraft} isAdmin={isAdmin} />

      {isAdmin && (
        <div className="flex items-center gap-3 sticky bottom-4">
          <button
            onClick={save}
            disabled={saving}
            className="btn-primary"
            data-testid="org-save-btn"
          >
            {saving ? <><Loader2 className="animate-spin" size={14} /> Saving…</>
                    : <><Check size={14} /> Save changes</>}
          </button>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testId, disabled, textarea }) {
  const Tag = textarea ? "textarea" : "input";
  return (
    <div>
      <label className="block text-[12px] text-[var(--ink-3)] mb-1.5">{label}</label>
      <Tag
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        rows={textarea ? 3 : undefined}
        className="input w-full"
        data-testid={testId}
      />
    </div>
  );
}

function EsignDefaultsSection({ draft, setDraft, isAdmin }) {
  const isDocuseal = (draft.default_esign_provider || "builtin") === "docuseal";
  const dsTemplates = useDocusealTemplates(isDocuseal);
  const showRawInput = dsTemplates.error || (!dsTemplates.loading && dsTemplates.templates.length === 0);
  return (
    <div>
      <h3 className="font-heading text-lg mb-3" style={{ fontWeight: 500 }}>eSign defaults</h3>
      <p className="text-[12px] text-[var(--ink-2)] mb-3 max-w-lg">
        When staff click <span className="font-mono">Send for signature</span> on a workflow stage,
        this is the default provider used. Each stage can override at design time.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-[12px] text-[var(--ink-3)] mb-1.5">Default provider</label>
          <select
            className="w-full bg-white border border-[var(--border)] rounded px-3 py-2 text-[14px]"
            value={draft.default_esign_provider || "builtin"}
            onChange={(e) => setDraft({ ...draft, default_esign_provider: e.target.value })}
            disabled={!isAdmin}
            data-testid="org-field-esign-provider"
          >
            <option value="builtin">Built-in (typed signature)</option>
            <option value="docuseal">DocuSeal</option>
          </select>
        </div>
        {isDocuseal && (
          <div>
            <label className="block text-[12px] text-[var(--ink-3)] mb-1.5">Default DocuSeal template</label>
            {showRawInput ? (
              <input
                type="text" inputMode="numeric" placeholder="Template ID (DocuSeal offline)"
                className="w-full bg-white border border-[var(--border)] rounded px-3 py-2 text-[14px]"
                value={draft.default_docuseal_template_id}
                onChange={(e) => setDraft({ ...draft, default_docuseal_template_id: e.target.value.replace(/[^0-9]/g, "") })}
                disabled={!isAdmin}
                data-testid="org-field-esign-template"
              />
            ) : (
              <select
                className="w-full bg-white border border-[var(--border)] rounded px-3 py-2 text-[14px]"
                value={String(draft.default_docuseal_template_id || "")}
                onChange={(e) => setDraft({ ...draft, default_docuseal_template_id: e.target.value })}
                disabled={!isAdmin || dsTemplates.loading}
                data-testid="org-field-esign-template"
              >
                <option value="">{dsTemplates.loading ? "Loading templates…" : "Select a template…"}</option>
                {dsTemplates.templates.map((t) => (
                  <option key={t.id} value={String(t.id)}>{t.name}</option>
                ))}
              </select>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

